import { useState, useEffect, useContext } from 'react';
import * as echarts from 'echarts';
import useAxios from '../../../hooks/useAxios';
import AuthContext from '../../../contexts/auth-context';
import { Box, Typography } from '@mui/material';
import { urls } from '../../config/variables';
const DoctorExpensesLineChart = () => {
    const [chartData, setChartData] = useState({ period: [], value: { accepted: [], rejected: [] } });
    const [period, setPeriod] = useState('weekly');
    const { operation } = useAxios();
    const authCtx = useContext(AuthContext);
    const token = authCtx.token;

    const handleChangePeriod = (event) => {
        setPeriod(event.target.value);
    };

    const getDoctorExpenses = () => {
        operation(
            {
                method: 'GET',
                url: `${urls.ENCOUNTER_EXPENSES}?frequency_date=${period}`,
                headers: { Authorization: `token ${token}` },
            },
            (res) => {
                if (Array.isArray(res)) {
                    const periods = res.map((item) =>
                        new Date(item.f_date).toLocaleDateString('en-US', period === 'yearly' ? {year: "numeric"} : period === 'monthly' ? {month: 'short'} : {month: 'short', day: 'numeric'})
                    );
                    const accepted = res.map((item) => parseFloat(item.accepted_cost));
                    const rejected = res.map((item) => parseFloat(item.rejected_cost));

                    setChartData({
                        period: periods,
                        value: { accepted, rejected }
                    });
                }
            },
            (err) => console.log(err),
        );
    };

    useEffect(() => {
        getDoctorExpenses();
        // eslint-disable-next-line
    }, [period]);

    useEffect(() => {
        if (!chartData.period.length) return;

        const chartDom = document.getElementById('echart-container');
        const chart = echarts.init(chartDom);

        const option = {
            tooltip: {
                trigger: 'axis',
                confine: true,
                transitionDuration: 0,
                hideDelay: 0,
                formatter: (params) => {
                    const header = params[0]?.axisValueLabel || '';
                    const seen = new Set();
                    const lines = [header];

                    params.forEach((p) => {
                        const key = p.seriesName;
                        if (!seen.has(key)) {
                            seen.add(key);
                            lines.push(`${p.marker} ${p.seriesName}: $${p.data}`);
                        }
                    });

                    return lines.join('<br/>');
                },
            },
            legend: {
                data: ['Approved cost', 'Rejected cost'],
                top: '10px',
                right: '120px',
                icon: 'circle',
                itemGap: 30,
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '10%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: chartData.period,
            },
            yAxis: { 
                type: 'value',
                axisLabel: { formatter: (val) => `$${val}` },
            },
            series: [
                {
                    name: 'Rejected cost',
                    type: 'line',
                    data: chartData.value.rejected,
                    itemStyle: { color: '#F44336' },
                },
                {
                    name: 'Approved cost',
                    type: 'line',
                    data: chartData.value.accepted,
                    itemStyle: { color: '#4CAF50' },
                },
            ],
        };

        chart.setOption(option);

        const resizeObserver = new ResizeObserver(() => {
            chart.resize();
        });

        resizeObserver.observe(chartDom);

        return () => {
            resizeObserver.disconnect();
            chart.dispose();
        };
    }, [chartData]);

    return (
        <Box sx={{ position: 'relative' }}>
            <Typography sx={{ position: 'absolute', top: '4px', left: '10px', fontWeight: 500, fontSize: '16px' }}>
                Claims Trend
            </Typography>
            <Typography variant="h6" sx={{ position: 'absolute', top: '28px', left: '10px', fontSize: '12px', textAlign: 'center', color: '#86878C' }}>
                Approved vs rejected claim costs over time
            </Typography>

            <Box sx={{ position: 'absolute', top: '8px', right: '18px', width: '80px', height: '20px', zIndex: 1 }}>
                <select
                    value={period}
                    onChange={handleChangePeriod}
                    style={{
                        padding: '2px 12px',
                        borderRadius: '6px',
                        border: '2px solid #7C8DB51F',
                        backgroundColor: '#fff',
                        fontSize: '14px',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        outline: 'none',
                        cursor: 'pointer',
                        appearance: 'auto',
                    }}
                >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                </select>
            </Box>

            <Box id="echart-container" sx={{ height: '350px' }} />
        </Box>
    );
};

export default DoctorExpensesLineChart;
